/**
 * `<caos-topbar>` — the platform top bar (shell region `g`), reconstructing the Dragon OS
 * header layout recolored to the Atlantis `--caos-*` tokens.
 * (Catalog: Navigation › Topbar — the platform top bar: identity, workspace controls, and the app name.)
 *
 * Layout mirrors Dragon's `.os-topbar`: a three-column grid `auto 1fr auto`, `gap: 32px`,
 * `padding: 14px 28px`. The chrome paints the PAGE BASE colour (`--caos-color-bg`) — eggshell
 * in light, near-black in dark — so the bar reads continuous with the app background (Dragon's
 * rule: white/surface is reserved for DATA surfaces, never the chrome).
 *
 *   LEFT  (auto): the platform wordmark as a stacked lockup (primary word bold in the text
 *                 colour, secondary word in the accent, mono + wide-tracked) + an environment
 *                 chip (mono, uppercase, bordered). Sourced from the `--brand-wordmark` token
 *                 so a five-token white-label repaints it (ACME → "ACME OS").
 *   CENTER (1fr): a `center` slot (the reference app projects its global search here — the
 *                 platform is richer than Dragon, which has an empty centre).
 *   RIGHT (auto): an icon-button cluster (gap 16, justify-self:end) — a `commands` slot for extra
 *                 command icons the workspace projects (CAOS-348: an edge surface tucked up out of
 *                 the corner lands here), an optional Setup gear,
 *                 the theme toggle, and an account control (34px square avatar with initials on
 *                 an accent gradient + the user's name/sub) that opens a dropdown holding
 *                 "View my profile" and "Sign out". Icon buttons follow Dragon's `.os-cmd-trigger`
 *                 (1px border, subtle bg, icon always in the accent).
 *
 * It is a pure presentational element: it holds NO navigation or session logic. It emits
 * composed events the host wires to real handlers:
 *   `topbar-theme`     — cycle the theme
 *   `topbar-setup`     — open Setup
 *   `topbar-profile`   — open the signed-in user's profile
 *   `topbar-my-feedback` — open what this person has reported, and what happened to it
 *   `topbar-get-help`  — ask for help from wherever you are (CAOS-50). It sits in the account
 *                        menu rather than on an edge tab because every edge tab belongs to an
 *                        optional installed package, and a way to reach support has to be there
 *                        in an org that has installed nothing
 *   `topbar-edit-page` — edit the current page (only offered when `show-edit-page` is set)
 *   `topbar-avatar-item` — an entry another package contributed into the menu was chosen
 *   `topbar-settings`  — open the ACTIVE app's own Settings (only offered when `show-settings` is
 *                        set — CAOS-694/CAOS-342: gated on administering that app, distinct from
 *                        the global Setup gear)
 *   `topbar-signout`   — sign out
 *
 * Attributes (all optional strings): `user-name`, `user-sub`, `theme` (system|light|dark),
 * `env` (env-chip label), `wordmark` (override the `--brand-wordmark` token), and the booleans
 * `show-setup` (render the Setup gear), `show-edit-page` (render "Edit this page"), and
 * `show-settings` (render the per-app "Settings" entry). With no `user-name` the account block is
 * hidden.
 *
 * CAOS-581 — EVERY entry in the account menu is opt-IN for the host, and an entry the host has not
 * claimed is not rendered. The three submenu/optional entries are gated on the host supplying what
 * drives them: `personaOptions` for the "Viewing as" switcher, `impersonationOptions` for "Log in as
 * another user", `show-edit-page` for "Edit this page", `avatarMenuItems` for package contributions.
 * This is the whole contract: the component cannot perform any of these itself, so it must never
 * offer one that no host is listening for. A control that closes the menu and does nothing is the
 * defect this gating exists to prevent.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';
import { drawIcons, makeIcon } from './icons.js';

type ThemePref = 'system' | 'light' | 'dark';
const THEME_LABEL: Record<ThemePref, string> = { system: 'System', light: 'Light', dark: 'Dark' };

/* CAOS-602 — the header's marks come from the one vocabulary now. Ten of them lived here as SVG
   SOURCE, which is two problems rather than one: a second copy of glyphs the library already drew,
   and artwork arriving through `innerHTML`, which is exactly what the DOM-built vocabulary exists
   to avoid. The markup below leaves an empty `data-icon` holder for each and `drawIcons` fills
   them. Where a mark here and the vocabulary's disagreed in notation but not in shape — the tick,
   the forward chevron — the vocabulary's drawing is what ships, because one mark for one idea is
   the point. */
/** A permission-set group offered in the account menu's "Viewing as:" persona switcher. */
export interface TopbarPersona {
  key: string;
  label: string;
  description?: string;
}

/** A user the admin may view the app AS (the Login-As picker). */
export interface TopbarImpersonationTarget {
  id: string;
  label: string;
}

/**
 * CAOS-581 — an entry another package contributes into the account menu (the `avatar-menu` seam).
 * The workspace used to record these on the element and render none of them, so a contribution never
 * appeared where a person would look for it. Choosing one emits `topbar-avatar-item` with its key.
 */
export interface TopbarMenuContribution {
  key: string;
  label: string;
}

/** Two-letter initials from a display name (first + last word), Dragon's AccountMenu rule. */
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const sheet = makeSheet(`
  /* CAOS-581 — hiding "Edit this page" set the hidden attribute and changed nothing on screen,
     because .menu-item's own display:flex beat the browser's user-agent [hidden] rule. The guard
     that fixes it is NOT here: it is in the shared reset (base-element.ts RESET), which every
     component adopts before its own sheet, so no component can reopen the gap by declaring a
     display on something it hides. */

  :host {
    display: block;
    position: sticky;
    top: 0;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-chrome);
    /* The chrome reads the PAGE BASE, not a surface — Dragon's rule that the top bar is
       continuous with the app background (eggshell in light, near-black in dark). Frosted:
       a translucent base plus a blur so content scrolls under it like Dragon's .os-topbar.
       Matches Dragon's .os-topbar cascade WINNER (dragon-utilities.css:1941-1945):
       background rgba(0,0,0,0.4) + backdrop-filter blur(14px) — the later rule that lets the
       orb glow show through, NOT the earlier 0.75/blur(18px). Kept token-driven via a ~40%
       mix of the page base so light theme frosts the eggshell surface at equivalent translucency. */
    background: color-mix(in srgb, var(--caos-color-bg) 40%, transparent);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--caos-color-border);
  }

  .bar {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 32px;
    padding: 14px 28px;
    /* CAOS-377 — 63px here plus the hairline is the reference's 64px bar. Declared rather than left
       to whatever the tallest thing inside happens to measure, so the chrome cannot drift by a pixel
       when a control changes. */
    min-height: 63px;
    min-width: 0;
  }

  /* ── LEFT — brand lockup + env chip ─────────────────────────────────────── */
  .left { display: flex; align-items: center; gap: 12px; min-width: 0; }
  /* Dragon's .os-brand: a flex ROW (align-items:center) wrapping the stacked text block, so the
     two-line lockup is vertically centered in the bar exactly as Dragon renders it. */
  .brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    background: none;
    border: 0;
    padding: 0;
    cursor: default;
  }
  /* Dragon's .os-brand-text — the column that stacks the wordmark over the "OS" suffix. The
     column stretches both lines to the wordmark's width (default align-items:stretch), and
     text-align:center centres the shorter "OS" under the wider wordmark. Dragon gets this
     centring for free because its brand is a button (browsers default buttons to
     text-align:center, which inherits down); this component's brand is a span, so the
     centring is set explicitly here to match. */
  /* CAOS-522 — an org's uploaded/linked logo renders next to the text lockup (never in place of
     it) when --brand-logo-url resolves; sized to the two-line lockup's height, never distorted
     (contain). */
  .brand-logo { display: block; height: 28px; max-width: 180px; object-fit: contain; }
  .brand-text { display: flex; flex-direction: column; line-height: 1; text-align: center; }
  .brand-primary {
    font-family: var(--caos-font-display);
    font-weight: 800; /* Dragon .os-brand-primary is a literal 800 */
    font-size: 15px;
    color: var(--caos-color-text);
    letter-spacing: 0.04em;
  }
  /* CAOS-794 — no tagline: the suffix line is gone, so the company name grows to fill the same
     two-line vertical space rather than sitting small on just the top line. */
  .brand-text.no-tagline .brand-primary { font-size: 20px; }
  /* CAOS-795 — no company name: the primary line is gone, so the tagline grows to fill the same
     two-line vertical space and sits alone with no top-line gap above it. */
  .brand-text.no-wordmark .brand-secondary { font-size: 18px; margin-top: 0; }
  .brand-secondary {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    color: var(--caos-color-accent);
    letter-spacing: 0.24em;
    margin-top: 3px;
  }
  /* Dragon's .chip.warn: mono 10px uppercase .12em, a SOFT warn hairline (@30%), tight 2px radius,
     4px 10px padding, and NO background fill (transparent — just border + warn text, not a box). */
  .env {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--caos-font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--caos-color-warning-text);
    border: 1px solid color-mix(in srgb, var(--caos-color-warning) 30%, transparent);
    background: transparent;
    padding: 4px 10px;
    border-radius: var(--caos-radius-sm);
    white-space: nowrap;
  }
  .env[hidden] { display: none; }

  /* ── CENTER — flexible spacer (holds the projected slot) ─────────────────── */
  .center { display: flex; justify-content: center; min-width: 0; }

  /* ── RIGHT — icon-button cluster + account ──────────────────────────────── */
  .right {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-self: end;
  }

  /* Dragon's .os-cmd-trigger: PADDING-driven (8px 14px — wider than a fixed square), 1px border,
     subtle bg, tight 2px radius; the icon (the glyph) is always in the accent. */
  .cmd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 8px 14px;
    border: 1px solid var(--caos-color-border);
    /* Dragon's .os-cmd-trigger fill is rgba(255,255,255,0.02) — a barely-there lightening on the
       dark chrome that reads as transparent on the light/eggshell chrome (the button is a bordered
       box showing the page base, NOT a tinted surface). CAOS-652 — mixed from the chrome's own ink
       rather than written as white, so a brand's chrome reaches it; it never picks up a surface tint. */
    background: color-mix(in srgb, var(--caos-color-chrome-text) 2%, transparent);
    color: var(--caos-color-accent);
    font-size: 13px; /* Dragon's .os-cmd-trigger is 13px */
    line-height: 1;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;
  }
  /* lucide icon: 16px (Dragon's w-4 h-4), stroke inherits the accent colour from .cmd. */
  .cmd svg { width: 16px; height: 16px; display: block; }

  /* CAOS-348 — the "commands" slot: extra command icons the workspace projects into the right
     cluster (an edge surface tucked up out of the corner lands here). Slotted buttons cannot pick up
     the shadow .cmd class, so they are given the same treatment through ::slotted. */
  ::slotted(button[slot="commands"]) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border: 1px solid var(--caos-color-border);
    background: color-mix(in srgb, var(--caos-color-chrome-text) 2%, transparent);
    color: var(--caos-color-accent);
    font-size: 13px;
    line-height: 1;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;
  }
  ::slotted(button[slot="commands"]:hover) { border-color: var(--caos-color-border-strong); }
  ::slotted(button[slot="commands"]:focus-visible) {
    outline: none;
    box-shadow: var(--caos-focus-ring);
    border-color: var(--caos-color-accent);
  }
  /* Dragon hover: strengthen the border only — the fill does NOT change. */
  .cmd:hover { border-color: var(--caos-color-border-strong); }
  .cmd:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-color: var(--caos-color-accent); }

  /* Account control — avatar + name/sub, opens the dropdown. A vertical hairline (Dragon's
     .os-user border-left + padding-left) separates the icon-button cluster from the account. */
  .account {
    position: relative;
    display: flex;
    align-items: center;
    padding-left: 16px;
    border-left: 1px solid var(--caos-color-border);
  }
  .account[hidden] { display: none; }
  .account-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: inherit;
  }
  .avatar {
    width: 34px;
    height: 34px;
    border-radius: var(--caos-radius-sm);
    background: linear-gradient(135deg, var(--caos-color-accent), var(--caos-color-accent-strong));
    color: var(--caos-color-text);
    display: grid;
    place-items: center;
    font-family: var(--caos-font-display);
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
  }
  /* Name STACKED over the sub-line (Dragon's name-over-role): a column so the sub wraps beneath
     the name instead of rendering inline beside it. */
  .who { display: flex; flex-direction: column; text-align: left; min-width: 0; }
  .who-name { font-size: 13px; color: var(--caos-color-text); font-weight: 600; white-space: nowrap; }
  .who-sub {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    color: var(--caos-color-text-ghost);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }

  /* CAOS-771 — the Setup gear is a menu trigger now, so it needs the same positioning context
     the account block has: .menu is absolutely positioned against its offset parent. The BLOCK
     is what gets hidden, not the button, so the whole control disappears together. */
  .setup-block { position: relative; display: inline-flex; }
  .setup-block[hidden] { display: none; }

  /* Dropdown menu (Dragon AccountMenu shape). */
  .menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    min-width: 260px;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: 10px;
    box-shadow: var(--caos-shadow-float);
    padding: 6px;
  }
  .menu[hidden] { display: none; }
  .menu-head { padding: 8px 12px 10px; }
  .menu-head-name { color: var(--caos-color-text); font-weight: 700; font-size: 14px; }
  .menu-head-sub { color: var(--caos-color-text-muted); font-size: 12px; margin-top: 1px; overflow-wrap: anywhere; }
  .menu-sep { height: 1px; background: var(--caos-color-border); margin: 2px 0; }
  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    padding: 9px 12px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--caos-color-text);
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
  }
  .menu-item:hover { background: var(--caos-color-surface-2); }
  .menu-item .glyph { color: var(--caos-color-text-muted); display: inline-flex; width: 14px; height: 14px; align-items: center; justify-content: center; }
  .menu-item .glyph svg { width: 14px; height: 14px; display: block; }

  /* Persona / impersonate submenu blocks (Dragon AccountMenu "Viewing as:" shape). Hidden until
     the host supplies the list (persona = permission groups; impersonate = org people). */
  .persona-block[hidden], .impersonate-block[hidden], .menu-sep[hidden] { display: none; }
  /* The expandable toggle reuses .menu-item; its label is a flex run with a trailing caret. */
  .menu-item .grow { flex: 1 1 auto; min-width: 0; }
  .menu-item .grow .muted { color: var(--caos-color-text-muted); }
  .menu-item .grow .strong { font-weight: 600; }
  .menu-item .grow .hint { color: var(--caos-color-text-muted); font-size: 11px; margin-left: 6px; }
  .caret { color: var(--caos-color-text-ghost); width: 14px; height: 14px; display: inline-flex; flex-shrink: 0; transition: transform 0.15s ease; }
  .caret svg { width: 14px; height: 14px; display: block; }
  .caret.open { transform: rotate(90deg); }

  .submenu { padding: 2px 4px 4px; }
  .submenu[hidden] { display: none; }
  .submenu-note { padding: 4px 10px 6px 22px; color: var(--caos-color-text-muted); font-size: 11px; line-height: 1.4; }
  .submenu-empty { padding: 7px 10px 7px 22px; color: var(--caos-color-text-muted); font-size: 12px; }
  .subitem {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
    text-align: left;
    padding: 7px 10px 7px 22px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--caos-color-text);
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
  }
  .subitem:hover { background: var(--caos-color-surface-2); }
  .subitem[aria-checked="true"] { background: var(--caos-color-border); }
  .subitem .subitem-body { flex: 1 1 auto; min-width: 0; }
  .subitem .subitem-name { display: flex; align-items: center; gap: 6px; font-weight: 600; }
  .subitem .subitem-name .tick { color: var(--caos-color-accent); width: 12px; height: 12px; display: inline-flex; }
  .subitem .subitem-name .tick svg { width: 12px; height: 12px; display: block; }
  .subitem .subitem-sub { color: var(--caos-color-text-muted); font-size: 11px; }
  .subitem.reset { color: var(--caos-color-text-muted); font-size: 12px; font-weight: 400; }
  /* the impersonate list can grow long — cap it and scroll, like Dragon's caret menus. */
  .submenu.scroll { max-height: 240px; overflow-y: auto; }

  /* Responsive — two tiers, mirroring Dragon's .os-topbar breakpoints.
     Tier 1 (below large): tighten the bar and drop the account name/sub, keeping the avatar.
     Tier 2 (below medium): tighten further, compact the command triggers, and fold the stacked brand
     lockup onto a single baseline-aligned row. The account control COLLAPSES to its avatar here —
     it is never hidden (CAOS-343): hiding it took sign-out and the profile away entirely on a
     phone, which the reference never does. */
  @media ${below('large')} {
    .bar { gap: 14px; padding: 12px 18px; }
    .who { display: none; }
  }
  @media ${below('medium')} {
    .bar { gap: 10px; padding: 10px 14px; }
    .right { gap: 8px; }
    .cmd { padding: 8px 10px; }
    .account-trigger { padding: 4px; }
    .brand-text { flex-direction: row; align-items: baseline; gap: 6px; }
    .brand-secondary { margin-top: 0; }
    /* The menu is anchored to the right edge of a narrow viewport, so pin it there and let it use
       the width it has rather than overflowing off-screen. */
    .menu { right: 0; min-width: min(280px, calc(100vw - 24px)); }
  }
`);

export class CaosTopbar extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'user-name',
      'user-sub',
      'theme',
      'env',
      'show-setup',
      'show-edit-page',
      'show-settings',
      'wordmark',
      'os-suffix',
      'active-persona-key',
      'persona-label',
    ];
  }

  private els!: {
    brandLogo: HTMLImageElement;
    brandText: HTMLElement;
    brandPrimary: HTMLElement;
    brandSecondary: HTMLElement;
    env: HTMLElement;
    right: HTMLElement;
    setupBtn: HTMLButtonElement;
    setupBlock: HTMLElement;
    setupMenu: HTMLElement;
    setupOpen: HTMLButtonElement;
    editPageSep: HTMLElement;
    themeBtn: HTMLButtonElement;
    editPage: HTMLButtonElement;
    settings: HTMLButtonElement;
    settingsSep: HTMLElement;
    contributed: HTMLElement;
    account: HTMLElement;
    avatar: HTMLElement;
    whoName: HTMLElement;
    whoSub: HTMLElement;
    menu: HTMLElement;
    menuName: HTMLElement;
    menuSub: HTMLElement;
    personaSep: HTMLElement;
    personaBlock: HTMLElement;
    personaToggle: HTMLButtonElement;
    personaLabel: HTMLElement;
    personaCaret: HTMLElement;
    personaSubmenu: HTMLElement;
    impersonateBlock: HTMLElement;
    impersonateToggle: HTMLButtonElement;
    impersonateCaret: HTMLElement;
    impersonateSubmenu: HTMLElement;
  };
  private menuOpen = false;
  private setupMenuOpen = false;
  private personaOpen = false;
  private impersonateOpen = false;
  private personas: TopbarPersona[] = [];
  private impersonationTargets: TopbarImpersonationTarget[] = [];
  private contributedItems: TopbarMenuContribution[] = [];
  // The two branches are independent because at most one of the menus is ever open — setMenu and
  // setSetupMenu each close the other. Nothing at this call site enforces that, so if the mutual
  // exclusion below is ever relaxed, this needs revisiting rather than silently closing both.
  private readonly onDocPointer = (e: MouseEvent) => {
    const path = e.composedPath();
    if (this.menuOpen && !path.includes(this.els.account)) this.setMenu(false);
    if (this.setupMenuOpen && !path.includes(this.els.setupBlock)) this.setSetupMenu(false);
  };
  private readonly onDocKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    this.setMenu(false);
    this.setSetupMenu(false);
  };
  /** Re-sync when the root's brand generation changes (`data-brand`). The wordmark and OS-suffix
   *  are resolved from inherited `--brand-*` tokens, which are NOT this element's own attributes,
   *  so the standard attributeChangedCallback never fires for a brand swap — this observer bridges
   *  that so a white-label switch repaints the lockup live, with no reload. */
  private brandObserver: MutationObserver | null = null;

  constructor() {
    super(sheet);
    const bar = document.createElement('div');
    bar.className = 'bar';
    bar.innerHTML = `
      <div class="left">
        <span class="brand" part="brand">
          <img class="brand-logo" part="brand-logo" alt="" hidden />
          <span class="brand-text">
            <span class="brand-primary" part="brand-primary"></span>
            <span class="brand-secondary" part="brand-secondary"></span>
          </span>
        </span>
        <span class="env" part="env" hidden></span>
      </div>
      <div class="center"><slot name="center"></slot></div>
      <div class="right">
        <slot name="commands"></slot>
        <div class="setup-block" part="setup-block" hidden>
          <button type="button" class="cmd setup" part="setup" title="Setup" aria-label="Setup"
                  aria-haspopup="menu" aria-expanded="false" data-icon="settings"></button>
          <div class="menu setup-menu" role="menu" aria-label="Setup menu" hidden>
            <button type="button" role="menuitem" class="menu-item setup-open">
              <span class="glyph" aria-hidden data-icon="settings"></span><span>Setup</span>
            </button>
            <div class="menu-sep edit-page-sep" hidden></div>
            <button type="button" role="menuitem" class="menu-item edit-page" hidden>
              <span class="glyph" aria-hidden data-icon="square-pen"></span><span>Edit this page</span>
            </button>
          </div>
        </div>
        <button type="button" class="cmd theme" part="theme" aria-label="Change theme" data-icon="moon"></button>
        <div class="account" part="account" hidden>
          <button type="button" class="account-trigger" aria-haspopup="menu" aria-expanded="false" title="Account">
            <span class="avatar" aria-hidden></span>
            <span class="who">
              <span class="who-name"></span>
              <span class="who-sub"></span>
            </span>
          </button>
          <div class="menu" role="menu" aria-label="Account menu" hidden>
            <div class="menu-head">
              <div class="menu-head-name"></div>
              <div class="menu-head-sub"></div>
            </div>
            <div class="menu-sep"></div>
            <button type="button" role="menuitem" class="menu-item profile">
              <span class="glyph" aria-hidden data-icon="user-round"></span><span>View my profile</span>
            </button>
            <button type="button" role="menuitem" class="menu-item get-help">
              <span class="glyph" aria-hidden data-icon="circle-help"></span><span>Get help</span>
            </button>
            <button type="button" role="menuitem" class="menu-item my-feedback">
              <span class="glyph" aria-hidden data-icon="message-square-text"></span><span>My feedback</span>
            </button>

            <!-- Entries other packages contribute into this menu (the avatar-menu seam). -->
            <div class="contributed"></div>

            <div class="menu-sep persona-sep" hidden></div>

            <!-- Persona switcher — Dragon's AccountMenu "Viewing as:" submenu (client-side preview). -->
            <div class="persona-block" hidden>
              <button type="button" role="menuitem" class="menu-item persona-toggle" aria-haspopup="menu" aria-expanded="false">
                <span class="grow"><span class="muted">Viewing as:</span> <span class="strong persona-label"></span><span class="hint">(preview)</span></span>
                <span class="caret" aria-hidden data-icon="chevron-right"></span>
              </button>
              <div class="submenu persona-submenu" role="menu" aria-label="Switch persona" hidden></div>
            </div>

            <!-- Impersonation start — admin Login-As picker (Dragon's UI-205 "Login As"). -->
            <div class="impersonate-block" hidden>
              <button type="button" role="menuitem" class="menu-item impersonate-toggle" aria-haspopup="menu" aria-expanded="false">
                <span class="glyph" aria-hidden data-icon="eye"></span><span class="grow">Log in as another user</span>
                <span class="caret" aria-hidden data-icon="chevron-right"></span>
              </button>
              <div class="submenu scroll impersonate-submenu" role="menu" aria-label="Log in as another user" hidden></div>
            </div>

            <!-- CAOS-694/CAOS-342 — the ACTIVE app's own Settings (opt-in, host-gated: administers
                 that app AND it declares a settings surface). Its own separator, matching the
                 reference's own divider before Sign out — present only alongside the entry itself. -->
            <div class="menu-sep settings-sep" hidden></div>
            <button type="button" role="menuitem" class="menu-item settings" hidden>
              <span class="glyph" aria-hidden data-icon="settings"></span><span>Settings</span>
            </button>

            <button type="button" role="menuitem" class="menu-item signout">
              <span class="glyph" aria-hidden data-icon="log-out"></span><span>Sign out</span>
            </button>
          </div>
        </div>
      </div>
    `;
    drawIcons(bar);
    this.root.append(bar);

    this.els = {
      brandLogo: bar.querySelector('.brand-logo')!,
      brandText: bar.querySelector('.brand-text')!,
      brandPrimary: bar.querySelector('.brand-primary')!,
      brandSecondary: bar.querySelector('.brand-secondary')!,
      env: bar.querySelector('.env')!,
      right: bar.querySelector('.right')!,
      setupBtn: bar.querySelector('.setup')!,
      setupBlock: bar.querySelector('.setup-block')!,
      setupMenu: bar.querySelector('.setup-menu')!,
      setupOpen: bar.querySelector('.menu-item.setup-open')!,
      editPageSep: bar.querySelector('.edit-page-sep')!,
      themeBtn: bar.querySelector('.theme')!,
      editPage: bar.querySelector('.menu-item.edit-page')!,
      settings: bar.querySelector('.menu-item.settings')!,
      settingsSep: bar.querySelector('.settings-sep')!,
      contributed: bar.querySelector('.contributed')!,
      account: bar.querySelector('.account')!,
      avatar: bar.querySelector('.avatar')!,
      whoName: bar.querySelector('.who-name')!,
      whoSub: bar.querySelector('.who-sub')!,
      menu: bar.querySelector('.account .menu')!,
      menuName: bar.querySelector('.menu-head-name')!,
      menuSub: bar.querySelector('.menu-head-sub')!,
      personaSep: bar.querySelector('.persona-sep')!,
      personaBlock: bar.querySelector('.persona-block')!,
      personaToggle: bar.querySelector('.persona-toggle')!,
      personaLabel: bar.querySelector('.persona-label')!,
      personaCaret: bar.querySelector('.persona-toggle .caret')!,
      personaSubmenu: bar.querySelector('.persona-submenu')!,
      impersonateBlock: bar.querySelector('.impersonate-block')!,
      impersonateToggle: bar.querySelector('.impersonate-toggle')!,
      impersonateCaret: bar.querySelector('.impersonate-toggle .caret')!,
      impersonateSubmenu: bar.querySelector('.impersonate-submenu')!,
    };

    // CAOS-771 — the gear opens a menu; the "Setup" entry inside it is what navigates. Only one
    // of the two menus is ever open, so opening either closes the other.
    this.els.setupBtn.addEventListener('click', () => this.setSetupMenu(!this.setupMenuOpen));
    this.els.setupOpen.addEventListener('click', () => { this.setSetupMenu(false); this.emit('topbar-setup'); });
    this.els.themeBtn.addEventListener('click', () => this.emit('topbar-theme'));
    bar.querySelector('.account-trigger')!.addEventListener('click', () => this.setMenu(!this.menuOpen));
    bar.querySelector('.menu-item.profile')!.addEventListener('click', () => { this.setMenu(false); this.emit('topbar-profile'); });
    this.els.editPage.addEventListener('click', () => { this.setSetupMenu(false); this.emit('topbar-edit-page'); });
    bar.querySelector('.menu-item.get-help')!.addEventListener('click', () => { this.setMenu(false); this.emit('topbar-get-help'); });
    bar.querySelector('.menu-item.my-feedback')!.addEventListener('click', () => { this.setMenu(false); this.emit('topbar-my-feedback'); });
    this.els.settings.addEventListener('click', () => { this.setMenu(false); this.emit('topbar-settings'); });
    bar.querySelector('.menu-item.signout')!.addEventListener('click', () => { this.setMenu(false); this.emit('topbar-signout'); });
    // The two submenus toggle open/closed in place (only one at a time).
    this.els.personaToggle.addEventListener('click', () => this.setPersonaOpen(!this.personaOpen));
    this.els.impersonateToggle.addEventListener('click', () => this.setImpersonateOpen(!this.impersonateOpen));
  }

  connectedCallback(): void {
    document.addEventListener('mousedown', this.onDocPointer);
    document.addEventListener('keydown', this.onDocKey);
    // Watch the root for a brand-generation swap (data-brand); re-resolve the token-driven lockup.
    this.brandObserver = new MutationObserver(() => this.sync());
    this.brandObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-brand'] });
    this.sync();
  }

  disconnectedCallback(): void {
    document.removeEventListener('mousedown', this.onDocPointer);
    document.removeEventListener('keydown', this.onDocKey);
    this.brandObserver?.disconnect();
    this.brandObserver = null;
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  private setSetupMenu(open: boolean): void {
    this.setupMenuOpen = open;
    this.els.setupMenu.hidden = !open;
    this.els.setupBtn.setAttribute('aria-expanded', String(open));
    if (open) this.setMenu(false); // one menu at a time
  }

  private setMenu(open: boolean): void {
    this.menuOpen = open;
    if (open) this.setSetupMenu(false); // one menu at a time
    this.els.menu.hidden = !open;
    this.els.account.querySelector('.account-trigger')!.setAttribute('aria-expanded', String(open));
    // Collapse the nested submenus whenever the account menu closes.
    if (!open) {
      this.setPersonaOpen(false);
      this.setImpersonateOpen(false);
    }
  }

  private setPersonaOpen(open: boolean): void {
    this.personaOpen = open;
    this.els.personaSubmenu.hidden = !open;
    this.els.personaCaret.classList.toggle('open', open);
    this.els.personaToggle.setAttribute('aria-expanded', String(open));
    if (open) this.setImpersonateOpen(false); // one submenu at a time
  }

  private setImpersonateOpen(open: boolean): void {
    this.impersonateOpen = open;
    this.els.impersonateSubmenu.hidden = !open;
    this.els.impersonateCaret.classList.toggle('open', open);
    this.els.impersonateToggle.setAttribute('aria-expanded', String(open));
    if (open) this.setPersonaOpen(false);
  }

  /** The persona list (permission-set groups) — set by the host as a PROPERTY (an array can't ride
   *  an attribute). Empty ⇒ the "Viewing as:" block is hidden. Mirrors the launcher/workspace idiom. */
  set personaOptions(value: TopbarPersona[]) {
    this.personas = Array.isArray(value) ? value : [];
    this.renderPersonaSubmenu();
    this.sync();
  }
  get personaOptions(): TopbarPersona[] {
    return this.personas;
  }

  /** The Login-As target list (org people) — set by the host as a PROPERTY. Empty ⇒ the "Log in as
   *  another user" block is hidden (the host only populates it for a caller who may impersonate). */
  set impersonationOptions(value: TopbarImpersonationTarget[]) {
    this.impersonationTargets = Array.isArray(value) ? value : [];
    this.renderImpersonateSubmenu();
    this.sync();
  }
  get impersonationOptions(): TopbarImpersonationTarget[] {
    return this.impersonationTargets;
  }

  /**
   * CAOS-581 — the entries other packages contribute into this menu, set by the host as a PROPERTY.
   * They render as real menu items between the workspace's own entries and Sign out. Empty ⇒ nothing
   * is drawn (no heading over an empty list).
   */
  set avatarMenuItems(value: TopbarMenuContribution[]) {
    this.contributedItems = Array.isArray(value) ? value.filter((i) => i && i.key && i.label) : [];
    this.renderContributed();
  }
  get avatarMenuItems(): TopbarMenuContribution[] {
    return this.contributedItems;
  }

  private renderContributed(): void {
    const host = this.els.contributed;
    host.replaceChildren();
    for (const item of this.contributedItems) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'menuitem');
      btn.className = 'menu-item contributed-item';
      btn.dataset.sourceKey = item.key;
      const glyph = document.createElement('span');
      glyph.className = 'glyph';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.append(makeIcon('chevron-right'));
      const label = document.createElement('span');
      label.textContent = item.label;
      btn.append(glyph, label);
      btn.addEventListener('click', () => {
        this.setMenu(false);
        this.emit('topbar-avatar-item', { key: item.key, label: item.label });
      });
      host.append(btn);
    }
  }

  private renderPersonaSubmenu(): void {
    const activeKey = this.getAttribute('active-persona-key');
    const submenu = this.els.personaSubmenu;
    submenu.replaceChildren();

    const note = document.createElement('div');
    note.className = 'submenu-note';
    // Dragon's exact preview caveat: the switch is a capability preview, not a data-scope change.
    note.textContent =
      'Preview — field, tab, and app visibility switches to the picked persona. Row-level data still reflects your own access.';
    submenu.append(note);

    if (this.personas.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'submenu-empty';
      empty.textContent = 'No personas defined.';
      submenu.append(empty);
      return;
    }

    for (const p of this.personas) {
      const isActive = p.key === activeKey;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'menuitemradio');
      btn.setAttribute('aria-checked', String(isActive));
      btn.className = 'subitem';
      const body = document.createElement('span');
      body.className = 'subitem-body';
      const name = document.createElement('span');
      name.className = 'subitem-name';
      if (isActive) {
        const tick = document.createElement('span');
        tick.className = 'tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.append(makeIcon('check'));
        name.append(tick);
      }
      name.append(document.createTextNode(p.label));
      body.append(name);
      if (p.description) {
        const sub = document.createElement('span');
        sub.className = 'subitem-sub';
        sub.textContent = p.description;
        body.append(sub);
      }
      btn.append(body);
      btn.addEventListener('click', () => {
        this.setMenu(false);
        this.emit('topbar-persona', { key: p.key, label: p.label });
      });
      submenu.append(btn);
    }

    const reset = document.createElement('button');
    reset.type = 'button';
    reset.setAttribute('role', 'menuitem');
    reset.className = 'subitem reset';
    reset.textContent = 'Reset to default';
    reset.addEventListener('click', () => {
      this.setMenu(false);
      this.emit('topbar-persona', { key: null, label: null });
    });
    submenu.append(reset);
  }

  private renderImpersonateSubmenu(): void {
    const submenu = this.els.impersonateSubmenu;
    submenu.replaceChildren();

    const note = document.createElement('div');
    note.className = 'submenu-note';
    note.textContent = 'View the app as another user. Your own session is unchanged.';
    submenu.append(note);

    if (this.impersonationTargets.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'submenu-empty';
      empty.textContent = 'No users available.';
      submenu.append(empty);
      return;
    }

    for (const t of this.impersonationTargets) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'menuitem');
      btn.className = 'subitem';
      const body = document.createElement('span');
      body.className = 'subitem-body';
      const name = document.createElement('span');
      name.className = 'subitem-name';
      name.textContent = t.label;
      body.append(name);
      btn.append(body);
      btn.addEventListener('click', () => {
        this.setMenu(false);
        this.emit('topbar-impersonate', { id: t.id, label: t.label });
      });
      submenu.append(btn);
    }
  }

  /** Resolve the wordmark: the `wordmark` attribute, else the inherited `--brand-wordmark`
   *  token, a CSS string literal whose surrounding quotes are stripped (vocabulary-ok: quotes as in quotation marks).
   *  No JS-level fallback here — see
   *  {@link resolveOsSuffix}'s comment (CAOS-795): the compiled floor's own `:root` default already
   *  supplies a real name when an org has never customized the token, so forcing one in code here
   *  would make it impossible for an org to explicitly clear Company Name (CAOS-795), the same bug
   *  CAOS-794 fixed for Tagline. */
  private resolveWordmark(): string {
    const attr = this.getAttribute('wordmark');
    if (attr && attr.trim()) return attr.trim();
    const token = getComputedStyle(this).getPropertyValue('--brand-wordmark').trim();
    return token.replace(/^["']|["']$/g, '').trim();
  }

  /** Resolve the accent suffix line: the `os-suffix` attribute, else the inherited
   *  `--brand-os-suffix` token. No JS-level fallback to "OS" here — the compiled floor's own
   *  `:root` default already supplies "OS" when an org has never customized the token, so
   *  `getComputedStyle` naturally returns it. That leaves room for the OTHER real state: an org
   *  that explicitly clears its Tagline saves an empty override (CAOS-794), which must resolve
   *  to genuinely empty, not get forced back to "OS" by this method. */
  private resolveOsSuffix(): string {
    const attr = this.getAttribute('os-suffix');
    if (attr && attr.trim()) return attr.trim();
    const token = getComputedStyle(this).getPropertyValue('--brand-os-suffix').trim();
    return token.replace(/^["']|["']$/g, '').trim();
  }

  /** CAOS-522 — resolve the org's brand logo image URL from the inherited `--brand-logo-url`
   *  token (a CSS string literal — strip its surrounding quotes (vocabulary-ok: as in quotation marks)).
   *  Empty when no logo is set, which keeps the text lockup as the lockup — a logo is additive,
   *  never required. */
  private resolveLogoUrl(): string {
    const token = getComputedStyle(this).getPropertyValue('--brand-logo-url').trim();
    return token.replace(/^["']|["']$/g, '').trim();
  }

  private sync(): void {
    // Brand lockup — company name on top (bold, text colour), platform suffix ("OS" default) on
    // the accent line below. Mirrors Dragon's DRAGON / OS. CAOS-794/795 — either line may be an
    // explicit empty override (distinct from never having customized it), and a white-label
    // deployment can clear BOTH to show only its logo — the four-state matrix:
    //   both set        → normal two-line lockup
    //   wordmark blank  → tagline alone, enlarged to fill both lines (CAOS-795)
    //   suffix blank    → company name alone, enlarged to fill both lines (CAOS-794)
    //   both blank      → the text lockup hides entirely; the logo (if any) is all that shows
    const wordmark = this.resolveWordmark();
    const osSuffix = this.resolveOsSuffix();
    const bothBlank = wordmark.length === 0 && osSuffix.length === 0;
    this.els.brandPrimary.textContent = wordmark;
    this.els.brandSecondary.textContent = osSuffix;
    this.els.brandText.hidden = bothBlank;
    this.els.brandPrimary.hidden = wordmark.length === 0;
    this.els.brandSecondary.hidden = osSuffix.length === 0;
    this.els.brandText.classList.toggle('no-tagline', !bothBlank && osSuffix.length === 0);
    this.els.brandText.classList.toggle('no-wordmark', !bothBlank && wordmark.length === 0);

    // CAOS-522 — an org-supplied logo image renders ALONGSIDE the text lockup (`.brand`'s flex
    // row lays them out side by side) whenever the lockup is showing — the logo is additive, not
    // a replacement. While the lockup shows, the logo is decorative (`alt=""`): the adjacent
    // visible text already conveys the name, and announcing it twice is noise.
    //
    // CAOS-797 — the both-blank state is the exception, and this comment used to argue the
    // opposite: that with the text hidden there was "no name left to announce either", so a
    // conditional here would be dead code. That reads the wrong way round. Hiding the text does
    // not remove the need for a name — it removes the only thing supplying one, and leaves the
    // logo as the sole conveyor of org identity on screen. `alt=""` then gives the whole brand
    // region no accessible name at all, and `.brand` is a plain <span> with no handler, so there
    // is no landmark or control to hang one on instead.
    //
    // The name is generic because nothing org-specific is available in this state — the org
    // deliberately blanked both text fields, and this component is never told the org's display
    // name by any other route. A generic functional description beats no name at all (WCAG).
    const logoUrl = this.resolveLogoUrl();
    if (logoUrl) {
      this.els.brandLogo.src = logoUrl;
      this.els.brandLogo.alt = bothBlank ? 'Company logo' : '';
      this.els.brandLogo.hidden = false;
    } else {
      this.els.brandLogo.hidden = true;
      this.els.brandLogo.removeAttribute('src');
    }

    // Environment chip.
    const env = this.getAttribute('env');
    if (env && env.trim()) {
      this.els.env.textContent = env.trim();
      this.els.env.hidden = false;
    } else {
      this.els.env.hidden = true;
    }

    // Theme toggle icon — Dragon's convention: the button shows the DESTINATION affordance,
    // a Moon while light/system (→ go dark) and a Sun while dark (→ go light), using the same
    // lucide Sun/Moon icons Dragon renders. The title/label still name the CURRENT preference.
    const theme = (this.getAttribute('theme') as ThemePref) || 'system';
    const pref: ThemePref = theme === 'light' || theme === 'dark' ? theme : 'system';
    this.els.themeBtn.replaceChildren(makeIcon(pref === 'dark' ? 'sun' : 'moon'));
    this.els.themeBtn.title = `Theme: ${THEME_LABEL[pref]} — click to change`;
    this.els.themeBtn.setAttribute('aria-label', `Theme: ${THEME_LABEL[pref]}. Change theme.`);

    // CAOS-771 — the Setup gear opens a flyout rather than navigating on its own click. Two
    // entries can live in it, each opt-IN for the host, so the gating is per-ENTRY and the block
    // itself is shown whenever either entry is offered.
    //
    // The block is not gated on `show-setup` alone. CAOS-581's rule is that a control the host
    // has not claimed is never rendered; its mirror is that a control the host HAS claimed is
    // never unreachable. Hiding the gear whenever `show-setup` is absent would bury an
    // "Edit this page" the host had asked for behind an icon that does not exist.
    const showSetup = this.hasAttribute('show-setup');

    // CAOS-581 — "Edit this page" is offered only when a host says it has an editor to open, the
    // same opt-IN shape as the Setup entry. It used to render unconditionally and emit an event
    // nothing listened for, so pressing it closed the menu and did nothing at all. There is no page
    // editor on the platform yet (Setup's Page Layouts is a read-only browse), so no host sets this.
    const showEditPage = this.hasAttribute('show-edit-page');

    this.els.setupOpen.hidden = !showSetup;
    this.els.editPage.hidden = !showEditPage;
    // The divider introduces "Edit this page", so it appears only when that entry does AND when
    // there is something above it to divide from.
    this.els.editPageSep.hidden = !(showEditPage && showSetup);
    this.els.setupBlock.hidden = !(showSetup || showEditPage);
    if (this.els.setupBlock.hidden) this.setSetupMenu(false);

    // CAOS-694/CAOS-342 — the ACTIVE app's own Settings, gated on the host actually supplying a
    // resolved destination (`show-settings`). Its separator tracks it 1:1 — no divider floats
    // alone when the entry it introduces is not offered.
    const showSettings = this.hasAttribute('show-settings');
    this.els.settings.hidden = !showSettings;
    this.els.settingsSep.hidden = !showSettings;

    // Account block.
    const name = this.getAttribute('user-name');
    const sub = this.getAttribute('user-sub') ?? '';
    if (name && name.trim()) {
      this.els.account.hidden = false;
      this.els.avatar.textContent = initialsFromName(name);
      this.els.whoName.textContent = name;
      this.els.whoSub.textContent = sub;
      this.els.menuName.textContent = name;
      this.els.menuSub.textContent = sub;
    } else {
      this.els.account.hidden = true;
      this.setMenu(false);
    }

    // Persona switcher — the "Viewing as:" line reflects the active persona. An explicit
    // `persona-label` wins; otherwise it is DERIVED from the active key against the offered personas,
    // so a host that sets the key alone does not read "Default" while a real persona is selected
    // (CAOS-342). Only a genuinely unknown persona falls back to "Default".
    const activePersonaKey = this.getAttribute('active-persona-key')?.trim();
    this.els.personaLabel.textContent =
      this.getAttribute('persona-label')?.trim() ||
      this.personas.find((p) => p.key === activePersonaKey)?.label ||
      'Default';
    // Keep the persona radio ticks in step with the active key (attribute-driven).
    this.renderPersonaSubmenu();

    // Block visibility: each submenu appears only when the host supplied its list. The shared
    // separator shows when EITHER block is present, so the account items above stay grouped.
    const showPersona = this.personas.length > 0;
    const showImpersonate = this.impersonationTargets.length > 0;
    this.els.personaBlock.hidden = !showPersona;
    this.els.impersonateBlock.hidden = !showImpersonate;
    this.els.personaSep.hidden = !(showPersona || showImpersonate);
  }
}

define('caos-topbar', CaosTopbar);

declare global {
  interface HTMLElementTagNameMap {
    'caos-topbar': CaosTopbar;
  }
}
